import React, { FC, PropsWithChildren, useEffect, useState } from "react";

import { PageProps } from "gatsby";

import Footer from "@layouts/Footer";
import Header from "@layouts/Header";
import ModalProvider from "@providers/ModalProvider";

import { Cart } from "@components/widgets";
import Modal from "@components/modal";
import useLocalStorageState from "use-local-storage-state";
import classNames from "classnames";
import { useDispatch } from "react-redux";
import { getItemsFromLocalStorage, hydrateSharedCart } from "@store/cart";
import WizardProvider from "@providers/WizardProvider";
import { StripeProvider } from "@providers/StripeProvider";
import TextSwitcher from "@components/common/text-switcher/TextSwitcher";
import Banner from "@components/common/Banner";
import { decodeSharedCartParam, sharedCartParamKey } from "@helpers/cart/shareableCart";

type Props = PropsWithChildren & PageProps;

const Wrapper = ({ children }: PropsWithChildren) => (
  <div style={{ marginBottom: 4 }}>{children}</div>
);

const texts = [
  {
    paragraph: (
      <Wrapper>
        <span className="black-reviews__text">
          100,000+ 5 Star Medipups Reviews ⭐️⭐️⭐️⭐️⭐️
        </span>
      </Wrapper>
    ),
  },
  {
    paragraph: (
      <Wrapper>
        <span className="black-reviews__text manufactured">
          Made In The USA from domestic & globally sourced ingredients
        </span>
      </Wrapper>
    ),
  },
  {
    paragraph: (
      <Wrapper>
        <span className="black-reviews__text">Free Shipping + 60 day money-back guarantee</span>
      </Wrapper>
    ),
  },
];

const Layout: FC<Props> = ({ children, location }) => {
  const [_, setGCLID] = useLocalStorageState<string>("mp-gclid");
  const [hasWindow, setHasWindow] = useState(false);

  const dispatch = useDispatch();

  useEffect(() => {
    setHasWindow(true);
  }, []);

  useEffect(() => {
    if (!hasWindow) return;

    const currentUrl = new URL(window.location.href);
    const shareParam = currentUrl.searchParams.get(sharedCartParamKey);
    const gclidParam = currentUrl.searchParams.get("gclid");
    let hydratedFromShare = false;

    if (shareParam) {
      const sharedCartItems = decodeSharedCartParam(shareParam);

      if (sharedCartItems?.length) {
        dispatch(hydrateSharedCart(sharedCartItems));
        hydratedFromShare = true;
      }

      currentUrl.searchParams.delete(sharedCartParamKey);
      const cleanedUrl = `${currentUrl.pathname}${currentUrl.search}${currentUrl.hash}`;
      window.history.replaceState({}, "", cleanedUrl);
    }

    if (!hydratedFromShare) {
      dispatch(getItemsFromLocalStorage());
    }

    if (gclidParam) {
      setGCLID(gclidParam);
    }
  }, [dispatch, hasWindow, setGCLID]);

  if (!hasWindow) return null;

  if (location.pathname.includes("/wizard")) {
    return (
      <WizardProvider>
        <main className="wizard-page-container">{children}</main>
      </WizardProvider>
    );
  }

  if (location.pathname.includes("/checkout")) {
    return (
      <ModalProvider>
        <Modal />
        <main className="checkout-page-container">{children}</main>
      </ModalProvider>
    );
  }

  if (location.pathname.includes("/thankyou")) {
    return (
      <StripeProvider>
        <ModalProvider>
          <Modal hideCloseButton />
          <main className="checkout-page-container">{children}</main>
        </ModalProvider>
      </StripeProvider>
    );
  }

  let locationName = location.pathname.slice(
    location.pathname.lastIndexOf("/", location.pathname.length - 2) + 1,
    location.pathname.length - 1
  );

  // TEMP SOLUTION FOR CALMING-CHEWS LANDING PAGE || NEED TO CHANGE IN THE FUTURE
  if (location.pathname === "/calming-chews" || location.pathname === "/calming-chews/") {
    locationName = "calming-chews-landing";
  } else if (
    location.pathname === "/shop/calming-chews" ||
    location.pathname === "/shop/calming-chews/"
  ) {
    locationName = "calming-chews";
  }

  return (
    <div className={classNames("root", `${locationName}-page`)}>
      <ModalProvider>
        <div className="black-reviews">
          <TextSwitcher texts={texts} />
        </div>
        <Header location={location} />
        <Banner />
        <Cart />
        <Modal />

        <main>{children}</main>

        <Footer />
      </ModalProvider>
    </div>
  );
};

export default Layout;
